import { Card } from '../../atoms/Card';
import { Stack } from '../../atoms/Stack';
import { Inline } from '../../atoms/Inline';
import { Input } from '../../atoms/Input/Input';
import { Select, type SelectOption } from '../../atoms/Select/Select';
import { Text, Heading } from '../../atoms/Typography';
import { FieldError } from '../../atoms/FieldError';
import { Button } from '../../atoms/Button';
import { LiveRegion } from '../../atoms/LiveRegion';
import { useTranslation } from '../../../i18n/useTranslation';
import styles from './TestSessionParameterRow.module.css';

export interface TestSessionParameterRowProps {
  /** Stable identifier for this row, used to derive element ids (e.g. `entries.0`). */
  rowId: string;
  /** Display name of the parameter, e.g. "KH". */
  parameterName: string;
  /** Current numeric value as a string (controlled input). */
  value: string;
  /** Currently-selected unit / test-kit-profile id. */
  unitProfileId: string;
  /** Options for the unit / test-kit-profile select. */
  unitProfileOptions: SelectOption[];
  /** Pre-formatted last-value hint text, e.g. "8.2 dKH · 3 days ago". May be empty. */
  lastValueHint?: string;
  /** Non-blocking plausibility warning text, e.g. "Did you mean 8.2?". Omit/undefined when there is none. */
  plausibilityWarning?: string;
  /** Called with the new raw value string whenever the value input changes. */
  onValueChange: (rowId: string, value: string) => void;
  /** Called with the new unit/profile id whenever the select changes. */
  onUnitChange: (rowId: string, unitProfileId: string) => void;
  /** Called when the remove button is activated. */
  onRemove: (rowId: string) => void;
  /** Called when Enter is pressed in the value input (used by the parent to move focus). */
  onValueKeyDown?: (rowId: string, event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Whether this row's remove button should be rendered (parent may hide it for the only row). */
  removable?: boolean;
}

export function TestSessionParameterRow({
  rowId,
  parameterName,
  value,
  unitProfileId,
  unitProfileOptions,
  lastValueHint,
  plausibilityWarning,
  onValueChange,
  onUnitChange,
  onRemove,
  onValueKeyDown,
  removable = true,
}: TestSessionParameterRowProps) {
  const { t } = useTranslation();

  const hintId = `${rowId}-hint`;
  const warningId = `${rowId}-warning`;

  const describedBy = plausibilityWarning ? `${hintId} ${warningId}` : hintId;

  return (
    <Card>
      <Stack gap="2" className={styles.row}>
        <Inline justify="between" align="center">
          <Heading level={3}>{parameterName}</Heading>
          {removable ? (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('testSession.row.removeAriaLabel', { parameterName })}
              onClick={() => onRemove(rowId)}
            >
              {t('testSession.row.remove')}
            </Button>
          ) : null}
        </Inline>
        <Inline gap="4" wrap className={styles.fieldsWrap}>
          <Input
            type="number"
            inputMode="decimal"
            id={`${rowId}-value`}
            label={t('testSession.field.value')}
            name={`${rowId}.value`}
            value={value}
            aria-describedby={describedBy}
            className={styles.valueField}
            onChange={event => onValueChange(rowId, event.target.value)}
            onKeyDown={event => onValueKeyDown?.(rowId, event)}
          />
          <Select
            id={`${rowId}-unit`}
            label={t('testSession.field.unitProfile')}
            name={`${rowId}.testKitProfileId`}
            value={unitProfileId}
            options={unitProfileOptions}
            className={styles.unitField}
            onChange={event => onUnitChange(rowId, event.target.value)}
          />
        </Inline>
        <Text id={hintId} variant="caption" color="secondary">
          {lastValueHint ?? ''}
        </Text>
        {plausibilityWarning ? (
          <LiveRegion>
            <FieldError id={warningId}>{plausibilityWarning}</FieldError>
          </LiveRegion>
        ) : null}
      </Stack>
    </Card>
  );
}
